import React from 'react'
import { observer } from 'mobx-react'

import withSessionContext, { SessionContext } from 'hocs/withSessionContext'
import TreeRowObject from './TreeRowObject'
import TreeRowArray from './TreeRowArray'
import TreeRowArraySlice from './TreeRowArraySlice'
import TreeRowScalar from './TreeRowScalar'
import TreeRowEnum from './TreeRowEnum'
import TreeRowEmpty from './TreeRowEmpty'

import parseTreePath, { IParsedPath } from 'utils/parseTreePath'

interface IProps {
  style: React.CSSProperties
  index: number
  data: {
    pathsToRender: string[]
    recordIds: string[]
  }
}

class TreeRow extends React.PureComponent<IProps> {
  context!: SessionContext

  isArraySlice = (parsedPath: IParsedPath) => {
    return (
      parsedPath.record === null &&
      Array.isArray(parsedPath.arraySliceIndices) &&
      Array.isArray(parsedPath.value)
    )
  }

  handleToggleCollapse = (path: string) => {
    const { selection } = this.context.session!

    this.handleSelect(path)

    if (selection.tree.isExpanded(path)) {
      selection.tree.collapse()
    } else {
      selection.tree.expand()
    }
  }

  handleSelect = (path: string) => {
    const { selection } = this.context.session!

    selection.tree.select(path)
  }

  render() {
    const { selection } = this.context.session!
    const {
      style,
      index,
      data: { pathsToRender, recordIds },
    } = this.props

    const path = pathsToRender[index]
    const parsedPath = parseTreePath(path, recordIds)
    const activePath = selection.tree.activePath
    const isExpanded = selection.tree.isExpanded(path)

    if (this.isArraySlice(parsedPath)) {
      return (
        <TreeRowArraySlice
          path={path}
          isSelected={path === activePath}
          isExpanded={isExpanded}
          startIndex={parsedPath.arraySliceIndices![0]} // Can assume arraySliceIndices will exist because of this.isArraySlice
          endIndex={parsedPath.arraySliceIndices![1]}
          value={parsedPath.value}
          isLoading={!parsedPath.value}
          style={style}
          onToggleCollapse={this.handleToggleCollapse}
        />
      )
    }

    if (parsedPath.field?.isList || Array.isArray(parsedPath.value)) {
      return (
        <TreeRowArray
          path={path}
          isSelected={path === activePath}
          isExpanded={isExpanded}
          fieldName={parsedPath.field!.name} // Can assume field will exist because of parseTreePath
          modelName={parsedPath.field!.typeAsLabel}
          value={parsedPath.value}
          isLoading={!parsedPath.value}
          style={style}
          onToggleCollapse={this.handleToggleCollapse}
        />
      )
    }

    if (parsedPath.field?.isRelation || parsedPath.record) {
      return (
        <TreeRowObject
          path={path}
          isSelected={path === activePath}
          isExpanded={isExpanded}
          fieldName={parsedPath.field?.name} // `field` may not exist for root records
          modelName={parsedPath.model!.name}
          value={parsedPath.value}
          isLoading={parsedPath.record?.value === null}
          style={style}
          onToggleCollapse={this.handleToggleCollapse}
        />
      )
    }

    if (parsedPath.field?.isEnum) {
      return (
        <TreeRowEnum
          path={path}
          isSelected={path === activePath}
          fieldName={parsedPath.field?.name}
          value={parsedPath.value}
          style={style}
          onClick={this.handleSelect}
        />
      )
    }

    return (
      <TreeRowScalar
        path={path}
        isSelected={path === activePath}
        fieldName={parsedPath.field?.name || `${parsedPath.index}`}
        value={parsedPath.value}
        isString={parsedPath.field?.isString || false}
        isInt={parsedPath.field?.isInt || false}
        isFloat={
          parsedPath.field?.isFloat || parsedPath.field?.isDecimal || false
        }
        isBoolean={parsedPath.field?.isBoolean || false}
        isDateTime={parsedPath.field?.isDateTime || false}
        style={style}
        onClick={this.handleSelect}
      />
    )
  }
}

export default observer(withSessionContext(TreeRow))
